import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import axios from "axios"
import { RootState } from "@/store"
import { AlarmSettingsState } from "@/store/alarm-settings/types"
import { AlarmSettings, AlarmSettingsData, AlarmSettingsRulePayload } from "@/types/alarm-settings"

const endpoint = (locationId: number) => `/locations/${locationId}/alarm-settings`

const state: AlarmSettingsState = {
    loading: false,
    saving: false,
    settings: null,
}

const mutations: MutationTree<AlarmSettingsState> = {
    setLoading(state, loading: boolean) {
        state.loading = loading
    },
    setSaving(state, saving: boolean) {
        state.saving = saving
    },
    setSettings(state, settings: AlarmSettings | null) {
        state.settings = settings
    },
}

const actions: ActionTree<AlarmSettingsState, RootState> = {
    async fetchSettings({ commit }, locationId: number) {
        commit("setLoading", true)
        commit("setSettings", null)

        try {
            const res = await axios.get<AlarmSettingsData>(endpoint(locationId))

            commit("setSettings", res.data.data)
        } catch (e) {
            console.error("Error while fetching alarm settings", e)
        } finally {
            commit("setLoading", false)
        }
    },
    async saveSettings(
        { commit, dispatch },
        { locationId, rules }: { locationId: number; rules: AlarmSettingsRulePayload[] },
    ) {
        commit("setSaving", true)

        try {
            const res = await axios.put<AlarmSettingsData>(endpoint(locationId), {
                data: { rules },
            })

            commit("setSettings", res.data.data)

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "alarm_settings.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (e) {
            console.error("Error while saving alarm settings", e)
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "alarm_settings.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit("setSaving", false)
        }
    },
}

const getters: GetterTree<AlarmSettingsState, RootState> = {
    loading: ({ loading }) => loading,
    saving: ({ saving }) => saving,
    settings: ({ settings }) => settings,
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
} as Module<AlarmSettingsState, RootState>
